import { Fragment, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@altimateai/lego";
import { useBreadcrumbContext } from "./BreadcrumbsProvider";
import { BreadcrumbItemData } from "./types";

const MAX_LABEL_LENGTH = 30;
/** Show ellipsis dropdown when item count exceeds this */
const COLLAPSE_THRESHOLD = 5;
/** Number of trailing items to always show (excluding the first) */
const VISIBLE_TAIL_COUNT = 2;

const truncatedLabel = (label: string) => {
  if (label.length <= MAX_LABEL_LENGTH) {
    return <>{label}</>;
  }

  const truncated = `${label.slice(0, MAX_LABEL_LENGTH)}...`;

  return (
    <Tooltip title={label}>
      <span>{truncated}</span>
    </Tooltip>
  );
};

export const Breadcrumbs = () => {
  const { items, pushBreadcrumb } = useBreadcrumbContext();
  const navigate = useNavigate();

  const handleBreadcrumbClick = useCallback(
    (href: string, label: string, e: React.MouseEvent) => {
      // Allow modifier-key clicks to behave normally (open in new tab, etc.)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      // Truncate stack by pushing existing pathname (triggers truncation logic)
      pushBreadcrumb({ pathname: href, label });
      navigate(href);
    },
    [pushBreadcrumb, navigate]
  );

  // Split items into visible head, collapsed middle, and visible tail
  const { head, collapsed, tail } = useMemo(() => {
    if (items.length < COLLAPSE_THRESHOLD) {
      return {
        head: items,
        collapsed: [] as BreadcrumbItemData[],
        tail: [] as BreadcrumbItemData[],
      };
    }
    return {
      head: items.slice(0, 1),
      collapsed: items.slice(1, items.length - VISIBLE_TAIL_COUNT),
      tail: items.slice(items.length - VISIBLE_TAIL_COUNT),
    };
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  const renderItem = (item: BreadcrumbItemData, index: number, isLast: boolean) => (
    <Fragment key={`${item.href ?? item.label}-${index}`}>
      <BreadcrumbItem>
        {isLast || !item.href ? (
          <BreadcrumbPage>{truncatedLabel(item.label)}</BreadcrumbPage>
        ) : (
          <BreadcrumbLink asChild>
            <Link to={item.href} onClick={e => handleBreadcrumbClick(item.href!, item.label, e)}>
              {truncatedLabel(item.label)}
            </Link>
          </BreadcrumbLink>
        )}
      </BreadcrumbItem>
      {!isLast && <BreadcrumbSeparator />}
    </Fragment>
  );

  // No collapsing needed
  if (collapsed.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          {head.map((item, index) => renderItem(item, index, index === items.length - 1))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Render with ellipsis dropdown for collapsed middle items
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {/* First item (always visible) */}
        {head.map((item, index) => renderItem(item, index, false))}

        {/* Collapsed middle items in dropdown */}
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger aria-label="Toggle collapsed breadcrumbs">
              <BreadcrumbEllipsis />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {collapsed.map((item, i) => (
                <DropdownMenuItem
                  key={`collapsed-${item.href ?? item.label}-${i}`}
                  onClick={() => {
                    if (item.href) {
                      pushBreadcrumb({ pathname: item.href, label: item.label });
                      navigate(item.href);
                    }
                  }}
                  disabled={!item.href}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator />

        {/* Tail items (always visible) */}
        {tail.map((item, i) => {
          const globalIndex = items.length - VISIBLE_TAIL_COUNT + i;
          return renderItem(item, globalIndex, globalIndex === items.length - 1);
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
